import React from 'react'

export type SetError<T> = <K extends keyof T>(key: K, value: string | null) => void
export type ClearError<T> = <K extends keyof T>(key: K) => void
export type Errors<T> = Record<keyof T, string | null>

type ReturnValue<T extends Record<string, any>> = {
    errors: Record<keyof T, string | null>
    setError: SetError<T>
    clearError: ClearError<T>
    resetErrors: () => void
}

// Returns an errors object of the same shape as the `state` that's passed in with
// each value initially set to `null`, allowing you to set the value to an error message
function useFormErrors<T extends Record<string, any>>(state: T): ReturnValue<T> {
    let initialErrors = {} as Errors<T>

    for (const key in state) {
        initialErrors[key] = null
    }

    const [errors, setErrorValue] = React.useState(initialErrors)

    function setError<K extends keyof T>(key: K, value: string | null) {
        setErrorValue((errors) => ({
            ...errors,
            [key]: value,
        }))
    }

    function clearError<K extends keyof T>(key: K) {
        setError(key, null)
    }

    function resetErrors() {
        setErrorValue(initialErrors)
    }

    return {errors, setError, clearError, resetErrors}
}

export default useFormErrors
